import { z } from 'zod';

export const levelingFrameworkAssessmentStatusSchema = z.enum(['PENDING', 'SUCCESS', 'FAILED']);

export const checklistItemStatusSchema = z.enum(['SATISFIED', 'PARTIAL', 'MISSING']);

export type ChecklistItemStatus = z.infer<typeof checklistItemStatusSchema>;

export const levelCriterionOptionSchema = z.enum([
    'SCOPE_AND_IMPACT',
    'AUTONOMY',
    'EXPERTISE',
    'PEOPLE_LEADERSHIP',
    'BUSINESS_IMPACT',
    'OTHER',
]);

export type LevelCriterionOption = z.infer<typeof levelCriterionOptionSchema>;

export const assessmentEvidenceSchema = z.object({
    documentId: z.string(),
    fileName: z.string(),
    quote: z.string(),
});

export type AssessmentEvidence = z.infer<typeof assessmentEvidenceSchema>;

const assessedLevelSchema = z.object({
    description: z.string().nullish(),
    descriptionEvidence: assessmentEvidenceSchema.nullish(),
    level: z.number().int().nullish(),
    title: z.string(),
});

export const assessedTrackSchema = z.object({
    evidence: assessmentEvidenceSchema,
    levels: assessedLevelSchema.array(),
    levelTitlesEvidence: assessmentEvidenceSchema.nullish(),
    levelTitlesStatus: checklistItemStatusSchema,
    name: z.string(),
});

export type AssessedTrack = z.infer<typeof assessedTrackSchema>;

const researchBasisSchema = z.enum(['COMPANY_PROFILE', 'JOB_ARCHITECTURE', 'WEB', 'COMMON_PRACTICE']);

const frameworkResearchSchema = z.object({
    criteria: z.object({ options: levelCriterionOptionSchema.array(), reason: z.string().nullish() }),
    facts: z.object({ basis: researchBasisSchema, sourceUrl: z.string().nullish(), text: z.string() }).array(),
    name: z.string().nullish(),
    tracks: z
        .object({ basis: researchBasisSchema, levelTitles: z.string().array(), name: z.string(), reason: z.string() })
        .array(),
});

export type FrameworkResearch = z.infer<typeof frameworkResearchSchema>;

export const levelingFrameworkAssessmentStageSchema = z.enum(['READING_FILES', 'RESEARCHING']);

export type LevelingFrameworkAssessmentStage = z.infer<typeof levelingFrameworkAssessmentStageSchema>;

export const levelingFrameworkAssessmentResultSchema = z.object({
    documents: z.object({ documentId: z.string(), fileName: z.string() }).array(),
    levelCriteria: z.object({
        criteria: z
            .object({ evidence: assessmentEvidenceSchema, label: z.string(), option: levelCriterionOptionSchema })
            .array(),
        status: checklistItemStatusSchema,
    }),
    levelDescriptionsStatus: checklistItemStatusSchema,
    levelTitlesStatus: checklistItemStatusSchema,
    name: z.object({
        evidence: assessmentEvidenceSchema.nullish(),
        status: checklistItemStatusSchema,
        value: z.string().nullish(),
    }),
    research: frameworkResearchSchema.nullish(),
    tracks: z.object({ status: checklistItemStatusSchema, tracks: assessedTrackSchema.array() }),
});

export type LevelingFrameworkAssessmentResult = z.infer<typeof levelingFrameworkAssessmentResultSchema>;

export const levelingFrameworkAssessmentResponseSchema = z.object({
    errorCode: z.string().nullish(),
    id: z.uuid(),
    result: levelingFrameworkAssessmentResultSchema.nullish(),
    stage: levelingFrameworkAssessmentStageSchema.nullish().catch(null),
    status: levelingFrameworkAssessmentStatusSchema,
});

export type LevelingFrameworkAssessmentResponse = z.infer<typeof levelingFrameworkAssessmentResponseSchema>;

export const levelingFrameworkGenerationResponseSchema = z.object({
    errorCode: z.string().nullish(),
    frameworkId: z.uuid().nullish(),
    id: z.uuid(),
    status: levelingFrameworkAssessmentStatusSchema,
});

export type LevelingFrameworkGenerationResponse = z.infer<typeof levelingFrameworkGenerationResponseSchema>;
